// Source: exams ui/primitives.tsx Button; decisions: soft variant → native daisyUI v5 btn-soft, error → solid btn-error, aria-busy while loading, native attrs via splitProps.
//
// ── THE VOCABULARY THE CODE USES (decision D1, 2026-09-16) ──────────────────
// daisyUI's names are canonical: `variant` is primary / secondary / ghost /
// outline / soft / error and `size` is `sm` (the default) or `md`. That is what
// the 254 production call sites pass (primary 90, ghost 67, outline 47,
// secondary 43, error 3, soft 2) and what this file's demo teaches. The Spectrum
// names — accent / neutral / negative, sizes s / m / l / xl, `pending` — remain
// as ALIASES for the Spectrum button study (apps/playground /design/buttons) and
// resolve to the same classes; a module should not reach for them.
//
//   variant     class          duty (spectrum.adobe.com/page/button)
//   ─────────   ────────────   ────────────────────────────────────────────
//   primary     btn-primary    the ACCENT — the one action the view is for
//   secondary   .btn (grey)    the companion to a primary; spectrum.css §3 inks it
//   ghost       btn-ghost      ACTIONBUTTON duty: toolbar verbs, dismiss, paging —
//                              a rounded SQUARE on the per-size radius ramp
//   outline     btn-outline    a stroke with no ground; low emphasis beside content
//   soft        btn-soft       the default when no variant is passed (a tinted ground)
//   error       btn-error      NEGATIVE — destructive, confirmed by a dialog first
//
// ── SHAPE IS THE SKIN'S, NOT THIS FILE'S ────────────────────────────────────
// Under @erp/ui/spectrum.css (imported by every portal) the corner geometry is
// §2's unlayered rule: every non-ghost .btn is a PILL by construction (Adobe's
// Button is calc(height/2)) and ghost takes the ActionButton ramp 7/8/9/10px.
// Both outrank any utility this component could paint. The `rounded-300`
// utility below is therefore only the fallback for a mount WITHOUT the skin
// (a test harness, a portal that forgot the import) — 6px, the libms shape —
// and `joined` suppresses even that inside a `join`, which owns its corners.
// `gap-1.5` is the same kind of fallback (§2's label-to-icon ramp outranks it).
// The former `pill` prop was a no-op under the skin and is gone (audit C-08).
//
// Sizes: `sm` = btn-sm = 32px desktop / 40 touch (Spectrum component-height-100),
// `md` = btn-md = 40 / 50. Heights ride daisyUI's own ramp, which already IS
// Spectrum's desktop ramp, and the touch scale lifts every step via one token.
//
// ── THE LINK FORM (2026-09-17, raw-btn sweep) ───────────────────────────────
// `href` renders the same skin as an `<a>`: a navigation that must LOOK like a
// button — "Open PDF" to a served proof, "Open reader" to an ebook in a new
// tab. Three consumer sites had pasted `class="btn …"` onto an <a> because the
// kit had no link form, which is exactly the recipe the raw-btn gate bans.
// A link has no disabled state, so `disabled` (or pending) withholds the href
// and marks aria-disabled. A same-app route is still a router <Link> wrapping
// a Button — the router owns preloading — never this.
import { Show, splitProps, type JSX } from 'solid-js';

/**
 * Canonical (daisyUI) names first — what production passes. The Spectrum
 * names after them are aliases kept for the button study: accent = primary,
 * negative = error, neutral = daisyUI's black `btn-neutral` (Spectrum's
 * "primary", which this system's brand ramp had already claimed).
 */
export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'outline'
  | 'soft'
  | 'error'
  // Spectrum aliases (apps/playground /design/buttons only)
  | 'accent'
  | 'neutral'
  | 'negative';

/** `sm` (default, 32px) or `md` (40px). `s`/`m`/`l`/`xl` are the Spectrum-scale aliases used by the button study: s = 24px, m = sm, l = md, xl = 48px. */
export type ButtonSize = 'sm' | 'md' | 's' | 'm' | 'l' | 'xl';

/** Fill = prominent. Outline = a stroke with no ground, for SECONDARY actions. */
export type ButtonTreatment = 'fill' | 'outline';

const VARIANT: Record<ButtonVariant, string> = {
  primary: 'btn-primary',
  secondary: '', // bare `.btn` is daisyUI's grey ground; spectrum.css tunes it
  ghost: 'btn-ghost',
  outline: 'btn-outline',
  soft: 'btn-soft',
  error: 'btn-error',
  // Spectrum aliases
  accent: 'btn-primary',
  neutral: 'btn-neutral',
  negative: 'btn-error',
};

const SIZE: Record<ButtonSize, string> = {
  sm: 'btn-sm', // 32px desktop / 40 touch — the default
  md: 'btn-md', // 40 / 50
  // Spectrum-scale aliases
  s: 'btn-xs', // 24 / 30
  m: 'btn-sm',
  l: 'btn-md',
  xl: 'btn-lg', // 48 / 60
};

export type ButtonProps = JSX.ButtonHTMLAttributes<HTMLButtonElement> & {
  /**
   * EMPHASIS. `primary` is the one action the view is for (one per view);
   * `secondary` its grey companion; `ghost` a toolbar/dismiss/paging verb
   * (ActionButton duty — square corners under the skin); `outline` a
   * low-emphasis action beside content; `error` a destructive verb, always
   * behind a ConfirmDialog. Default `soft`. See the header for the aliases.
   */
  variant?: ButtonVariant;
  /** `sm` (default) is the console's 32px control height; `md` (40px) only where a form's primary needs the taller rung. */
  size?: ButtonSize;
  /** Fill (default) or outline. Both draw the same edge, so switching is layout-free. */
  treatment?: ButtonTreatment;
  /**
   * A wait in progress. Under spectrum.css the label and icon go invisible —
   * keeping their box, so the button never resizes — and an indeterminate
   * circle centres over them; elsewhere the spinner simply precedes the label.
   * Reserve it for waits of a few seconds; anything longer wants its own
   * progress surface.
   */
  pending?: boolean;
  /** Alias of `pending` — the name the 32 production sites use. */
  loading?: boolean;
  /** A `join` member: leave the corners to the join, which owns its own radii (applies to the no-skin fallback only). */
  joined?: boolean;
  /**
   * THE LINK FORM: with `href` the button renders as an `<a>` with the same
   * skin — a served file or an external page that must read as a button
   * ("Open PDF", "Open reader"). `target` / `rel` / `download` ride beside it.
   * `disabled` withholds the href and sets aria-disabled (a link has no
   * disabled state). A same-app route is a router `<Link>` around a Button.
   */
  href?: string;
  /** Link form only: the browsing context — `_blank` for a served file, so the console stays put. */
  target?: '_self' | '_blank' | '_parent' | '_top' | (string & {});
  /** Link form only: `noreferrer` / `noopener` beside `target="_blank"`. */
  rel?: string;
  /** Link form only: the `download` attribute — the file name to save as. */
  download?: string;
};

/**
 * The class recipe a Button wears, for the ONE other element allowed to wear
 * it: a router `<Link>` that must read as a button (NotFoundCard's "Back to
 * …", a Home link). An anchor styled here is one tab stop; an `<a>` wrapping a
 * `<Button>` is two (audit rank 6), and Button's own `href` form is a plain
 * anchor with no router preloading. Kit-internal by intent — a module screen
 * still mounts Button or wraps one in Link.
 */
export const buttonClasses = (o: {
  variant?: ButtonVariant;
  size?: ButtonSize;
  treatment?: ButtonTreatment;
  joined?: boolean;
  class?: string;
}): string => {
  const variant = o.variant ?? 'soft';
  // No-skin fallback only — see the header: spectrum.css §2 owns the corners.
  const shape = o.joined ? '' : 'rounded-300';
  // `outline` on top of a coloured variant is daisyUI's own composition: it
  // blanks the ground and paints border + label in that variant's colour.
  const treatment = o.treatment === 'outline' && variant !== 'outline' ? 'btn-outline' : '';
  return `btn ${shape} ${VARIANT[variant]} ${SIZE[o.size ?? 'sm']} ${treatment} gap-1.5 ${o.class ?? ''}`;
};

export function Button(props: ButtonProps) {
  const [local, rest] = splitProps(props, [
    'variant',
    'size',
    'treatment',
    'pending',
    'loading',
    'joined',
    'class',
    'children',
    'disabled',
    'href',
    'target',
    'rel',
    'download',
    'type',
  ]);
  const variant = () => local.variant ?? 'soft';
  const busy = () => local.pending ?? local.loading ?? false;
  const inert = () => Boolean(local.disabled) || busy();
  const classes = () =>
    buttonClasses({
      variant: variant(),
      size: local.size,
      treatment: local.treatment,
      joined: local.joined,
      class: local.class,
    });
  const content = () => (
    <>
      <Show when={busy()}>
        <span class="loading loading-spinner loading-xs" aria-hidden="true" />
      </Show>
      {/* display:contents while pending ONLY — the wrapper is what
          `visibility:hidden` needs to grip (a bare text node has no box of its
          own), and `contents` keeps the flex layout byte-identical either way. */}
      <Show when={busy()} fallback={local.children}>
        <span class="contents">{local.children}</span>
      </Show>
    </>
  );
  return (
    <Show
      when={local.href !== undefined}
      fallback={
        <button
          type={local.type}
          class={classes()}
          data-variant={variant()}
          data-pending={busy() ? '' : undefined}
          disabled={inert()}
          aria-busy={busy() || undefined}
          {...rest}
        >
          {content()}
        </button>
      }
    >
      {/* The link form: no href while inert (an <a> without href is neither
          focusable nor a link), daisyUI's btn-disabled paints the same rest
          state a disabled <button> gets, and aria-disabled says why. */}
      <a
        class={`${classes()} ${inert() ? 'btn-disabled' : ''}`}
        data-variant={variant()}
        data-pending={busy() ? '' : undefined}
        href={inert() ? undefined : local.href}
        target={local.target}
        rel={local.rel}
        download={local.download}
        aria-disabled={inert() || undefined}
        aria-busy={busy() || undefined}
        {...(rest as JSX.HTMLAttributes<HTMLAnchorElement>)}
      >
        {content()}
      </a>
    </Show>
  );
}
